import React, { useState } from 'react';
import { Download, AlertCircle, Check, Loader2, Crown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { usePolling } from '../contexts/PollingContext';
import clientYouTubeExtractor from '../services/clientYouTubeExtractor';
import { videoApi } from '../services/api';
import { cleanUrl } from '../utils/urlCleaner';
import { apiOptimizer } from '../utils/apiOptimizer';

interface UrlImportCardProps {
  onImportStart?: (url: string, platform: 'youtube' | 'twitch' | 'kick') => void;
  onImportComplete?: () => void;
  onImportError?: () => void;
  className?: string;
  isPro?: boolean;
}

const UrlImportCard: React.FC<UrlImportCardProps> = ({ onImportStart, onImportComplete, onImportError, className = '', isPro = false }) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<'youtube' | 'twitch' | 'kick' | null>(null);
  const [currentMethod, setCurrentMethod] = useState<'server' | 'client' | 'mega-nuclear' | null>(null); // Track current attempt
  const { currentUser, refreshUserProfile } = useAuth();
  const { addUrlImportTask, addTask, updateTask, updateSyntheticProgress, clearSyntheticProgress, tasks: _tasks, updateTaskFromVideoStatus } = useUpload();
  const { addVideoStatusPolling, removeVideoStatusPolling: _removeVideoStatusPolling } = usePolling();
  const navigate = useNavigate();

  const detectPlatform = (inputUrl: string): 'youtube' | 'twitch' | 'kick' | null => {
    if (!inputUrl) return null;
    
    // YouTube patterns
    if (inputUrl.includes('youtube.com') || inputUrl.includes('youtu.be')) {
      return 'youtube';
    }
    
    // Twitch patterns
    if (inputUrl.includes('twitch.tv')) {
      return 'twitch';
    }
    
    // Kick patterns
    if (inputUrl.includes('kick.com')) {
      return 'kick';
    }
    
    return null;
  };

  const validateUrl = (inputUrl: string): boolean => {
    try {
      const urlObj = new URL(inputUrl);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    setSelectedPlatform(detectPlatform(newUrl));
  };

  const handleImport = async () => {
    if (!url.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }

    if (!validateUrl(url)) {
      toast.error('Please enter a valid URL (must start with http:// or https://)');
      return;
    }

    if (!selectedPlatform) {
      toast.error('Unsupported platform. Please use YouTube, Twitch, or Kick URLs');
      return;
    }

    // Check if user is trying to use pro-only platforms without pro
    if (!isPro && (selectedPlatform === 'twitch' || selectedPlatform === 'kick')) {
      navigate('/pricing');
      return;
    }

    setIsLoading(true);

    // For Twitch, Kick, or MEGA-NUCLEAR mode, use respective methods
    if (selectedPlatform === 'twitch' || selectedPlatform === 'kick') {
      setCurrentMethod('server');
      try {
        await handleServerSideImport();
        setIsLoading(false);
        onImportComplete?.();
      } catch (error) {
        // console.error(`${selectedPlatform} import failed:`, error);
        toast.error(error instanceof Error ? error.message : 'Failed to import video');
        setIsLoading(false);
        onImportError?.();
      }
      return;
    }

    // For YouTube: Try standard import, then MEGA-NUCLEAR 2025, then client-side
    // For Twitch: Just use standard server-side import
    if (selectedPlatform === 'youtube') {
      // Try standard server-side first
      setCurrentMethod('server');
      try {
        await handleServerSideImport();
        setIsLoading(false);
        onImportComplete?.();
        return;
      } catch (serverError) {
        const errorMessage = serverError instanceof Error ? serverError.message.toLowerCase() : '';
        // console.log('Standard server-side import failed:', serverError);
        
        // Check if it's a bot detection error that would benefit from advanced strategies
        const isBotDetectionError = 
          errorMessage.includes('sign in to confirm') ||
          errorMessage.includes('bot') ||
          errorMessage.includes('private or unavailable') ||
          errorMessage.includes('blocked') ||
          errorMessage.includes('not available') ||
          errorMessage.includes('failed to extract');

        if (isBotDetectionError) {
          // console.log('🚀 Bot detection detected, escalating to MEGA-NUCLEAR 2025...');
          
          // Try MEGA-NUCLEAR 2025 as fallback
          setCurrentMethod('mega-nuclear');
          try {
            await handleMegaNuclearImport();
            setIsLoading(false);
            onImportComplete?.();
            return;
          } catch (megaNuclearError) {
            // console.log('MEGA-NUCLEAR 2025 also failed, trying client-side fallback...');
            
            // Final fallback: client-side
            setCurrentMethod('client');
            try {
              await handleClientSideImport();
              setIsLoading(false);
              onImportComplete?.();
              return;
            } catch (clientError) {
              // All methods failed
              // console.error('All import methods failed:', { 
              //   serverError, 
              //   megaNuclearError, 
              //   clientError 
              // });
              
              toast.error('Unable to import this video. All advanced methods failed - the video may be heavily protected or unavailable.');
              setIsLoading(false);
              onImportError?.();
            }
          }
        } else {
          // Not a bot detection error - show the original error
          // console.error('Server-side import failed (not bot detection):', serverError);
          toast.error(serverError instanceof Error ? serverError.message : 'Failed to import video');
          setIsLoading(false);
          onImportError?.();
        }
      }
    } else {
      // For Twitch, just use standard server-side
      setCurrentMethod('server');
      try {
        await handleServerSideImport();
        setIsLoading(false);
      } catch (error) {
        // console.error('Twitch import failed:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to import video');
        setIsLoading(false);
      }
    }
  };

  const handleClientSideImport = async () => {
    try {
      // Client-side import requires auth for all platforms currently
      if (!currentUser) {
        toast.error('You must be logged in to import videos');
        return;
      }

      // Step 1: Extract video info and download in browser (silently)
      const cleanedUrl = cleanUrl(url.trim(), selectedPlatform || undefined);
      const videoInfo = await clientYouTubeExtractor.extractVideo(cleanedUrl);
      
      // Step 2: Create a File object from the blob
      const videoFile = new File([videoInfo.videoBlob], videoInfo.filename, {
        type: 'video/mp4'
      });

      // Step 3: Upload the file like a regular file upload
      const _taskId = addTask(videoFile);
      
      // The rest of the upload process is handled by the existing upload context
      // Navigate to the upload page to show progress
      navigate('/upload');
      
    } catch (error) {
      // console.error('Client-side import error:', error);
      
      // Just throw the error - let the parent handle messaging
      throw error;
    } finally {
      setIsLoading(false);
      setUrl('');
      setSelectedPlatform(null);
    }
  };

  const handleMegaNuclearImport = async () => {
    // Create task in upload context to show in floating widget
    const taskId = addUrlImportTask(url.trim(), selectedPlatform!);
    const tempVideoId = `temp_${taskId}`;
    let progressInterval: NodeJS.Timeout | null = null;
    
    try {
      // For YouTube, allow guest access; for Twitch/Kick, require auth
      if (!currentUser && selectedPlatform !== 'youtube') {
        toast.error(`You must be logged in to import from ${selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}`);
        return;
      }

      // Update task status with a temporary video ID for synthetic progress
      updateTask(taskId, { 
        status: 'uploading',
        uploadStatus: `🚀 Starting download...`,
        progress: 0,
        videoId: tempVideoId  // Temporary ID for synthetic progress tracking
      });
      
      // Start gradual synthetic progress animation for download phase
      let currentProgress = 0;
      const maxProgress = 50;
      progressInterval = setInterval(() => {
        if (currentProgress < maxProgress) {
          currentProgress += Math.random() * 2 + 0.5; // Slower progress for nuclear method
          currentProgress = Math.min(currentProgress, maxProgress);
          
          const progressMessages = [
            '🍪 Applying fresh cookies...',
            '🤖 Trying Android clients...',
            '📺 Testing TV embedded...',
            '🔧 Format 18 fallback...',
            '⚡ Multi-strategy approach...'
          ];
          const messageIndex = Math.floor(currentProgress / 20) % progressMessages.length;
          
          updateTask(taskId, {
            progress: Math.round(currentProgress),
            uploadStatus: `🚀 MEGA-NUCLEAR 2025: ${progressMessages[messageIndex]} ${Math.round(currentProgress)}%`
          });
          
          // Also update synthetic progress for consistency
          updateSyntheticProgress(tempVideoId, currentProgress, `🚀 MEGA-NUCLEAR 2025: ${progressMessages[messageIndex]}`);
        }
      }, 1500); // Update every 1.5 seconds (slower for dramatic effect)

      // Get Firebase auth token (empty for guests)
      const token = currentUser ? await currentUser.getIdToken() : '';
      
      // Get client info for enhanced bypass
      const clientInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timestamp: new Date().toISOString()
      };
      
      // Call the MEGA-NUCLEAR 2025 API endpoint
      const mnHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      };
      const response = await fetch('/api/v1/import-url', {
        method: 'POST',
        headers: mnHeaders,
        body: JSON.stringify({
          url: cleanUrl(url.trim(), selectedPlatform || undefined),
          platform: selectedPlatform,
          clientInfo: clientInfo
        }),
      });

      // Read text first to avoid JSON.parse errors on empty/HTML bodies
      const responseText = await response.text();
      if (!response.ok) {
        let detail = 'MEGA-NUCLEAR 2025 import failed';
        
        // Handle specific error codes
        if (response.status === 413) {
          detail = 'Upload limit reached! You\'ve reached your video upload limit for the free plan. Upgrade to continue importing videos.';
        } else {
          try {
            const errJson = JSON.parse(responseText);
            detail = errJson.detail || detail;
            
            // Clean up common quota error messages
            if (detail.toLowerCase().includes('video limit') || detail.toLowerCase().includes('limit reached')) {
              detail = 'Upload limit reached! You\'ve reached your video upload limit for the free plan. Upgrade to continue importing videos.';
            }
          } catch {
            const trimmed = (responseText || '').trim();
            const looksHtml = trimmed.startsWith('<') || trimmed.toLowerCase().includes('<html') || trimmed.toLowerCase().includes('doctype html');
            if (looksHtml || trimmed.length === 0) {
              detail = 'Temporary server error (502). We will continue in background if the job started.';
            } else {
              detail = trimmed;
            }
          }
        }
        throw new Error(detail);
      }
      let result: any;
      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error('Unexpected response from server (could not parse JSON)');
      }
      
      // Pre-populate cache with placeholder data to avoid empty state flash
      const placeholderVideo = {
        id: result.video_id,
        filename: result.filename || `import_pending_${result.video_id}.mp4`,
        status: 'processing',
        created_at: new Date().toISOString(),
        metadata: {
          duration: 0,
          file_size: 0,
          resolution: '1920x1080',
          codec: 'unknown',
          source_platform: selectedPlatform || 'youtube'
        },
        thumbnail_url: null,
        clips_count: 0
      };
      (apiOptimizer as any)['cache'].set(`video_${result.video_id}`, {
        data: placeholderVideo,
        timestamp: Date.now(),
        expiry: Date.now() + 30000 // 30 seconds TTL for placeholder
      });
      
      // Navigate immediately - no empty state!
      navigate(`/videos/${result.video_id}`);
      
      // Let the progress animation continue to 50% in background
      const waitForProgressAnimation = new Promise<void>((resolve) => {
        const checkProgress = () => {
          if (currentProgress >= 50) {
            // Clear synthetic progress for download phase and stop interval
            if (progressInterval) {
              clearInterval(progressInterval);
            }
            clearSyntheticProgress(tempVideoId);
            resolve();
          } else {
            setTimeout(checkProgress, 500);
          }
        };
        checkProgress();
      });
      
      await waitForProgressAnimation;
      
      // Immediately notify parent component to refresh dashboard
      if (onImportStart) {
        onImportStart(url, selectedPlatform!);
      }

      // Refresh user profile to update video count in UI
      refreshUserProfile();
      
      // Update task with video details and transition to processing
      updateTask(taskId, {
        videoId: result.video_id,
        fileName: result.filename || `YouTube Video Import`,
        status: 'analyzing',
        progress: 5, // Start low - backend will report actual progress
        uploadStatus: `🎉 MEGA-NUCLEAR 2025 SUCCESS! Processing video...`,
        processingStatus: `📥 Downloading and processing video...`
      });
      
      // Clear synthetic progress with temporary video ID since we now have the real one
      clearSyntheticProgress(tempVideoId);
      
      // Start video status polling to sync backend progress with frontend task
      addVideoStatusPolling(result.video_id, (statusData) => {
        // console.log(`🔄 [UrlImportCard MEGA-NUCLEAR] Received status update for ${result.video_id}:`, statusData);
        // Manually call updateTaskFromVideoStatus to ensure the widget gets updated
        updateTaskFromVideoStatus(result.video_id, statusData);
      }, 'high'); // High priority for active imports
      
      // Immediately check status to catch any progress that happened during the import call
      setTimeout(async () => {
        try {
          const currentStatus = await videoApi.getVideoStatus(result.video_id);
          // console.log(`🔍 [UrlImportCard MEGA-NUCLEAR] Immediate status check for ${result.video_id}:`, currentStatus);
          updateTaskFromVideoStatus(result.video_id, currentStatus);
        } catch (error) {
          // console.log(`⚠️ [UrlImportCard MEGA-NUCLEAR] Failed immediate status check:`, error);
        }
      }, 2000); // Check after 2 seconds
      
      toast.success(`🚀 MEGA-NUCLEAR 2025 import successful! Advanced bot protection bypassed.`);
      
      // Clear the form
      setUrl('');
      setSelectedPlatform(null);
      
    } catch (error) {
      // Clear synthetic progress on error and stop interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      clearSyntheticProgress(tempVideoId);

      // Attempt recovery: server may have created the job despite a 502 HTML response
      try {
        const videos = await videoApi.getVideos();
        if (Array.isArray(videos) && videos.length > 0) {
          const candidate = videos.find((v: any) => v.status === 'processing' || v.status === 'uploaded');
          if (candidate?.id) {
            updateTask(taskId, {
              status: 'analyzing',
              progress: 10,
              uploadStatus: 'Continuing import in background...',
              processingStatus: 'Continuing import in background...',
              videoId: candidate.id
            });
            // Navigate immediately to show loading states
            navigate(`/videos/${candidate.id}`);
            return;
          }
        }
      } catch {
        // Recovery failed; proceed to show sanitized error
      }

      const rawMessage = error instanceof Error ? error.message : 'MEGA-NUCLEAR 2025 import failed';
      const cleanMessage = rawMessage.replace(/<[^>]*>/g, '').trim().slice(0, 500) || 'MEGA-NUCLEAR 2025 import failed';
      updateTask(taskId, {
        status: 'error',
        error: cleanMessage,
        uploadStatus: '💀 MEGA-NUCLEAR 2025 failed'
      });
      throw new Error(cleanMessage);
    }
  };

  const handleServerSideImport = async () => {
    // Create task in upload context to show in floating widget
    const taskId = addUrlImportTask(url.trim(), selectedPlatform!);
    const tempVideoId = `temp_${taskId}`;
    let progressInterval: NodeJS.Timeout | null = null;
    
    try {
      // For YouTube, allow guest access; for Twitch/Kick, require auth
      if (!currentUser && selectedPlatform !== 'youtube') {
        toast.error(`You must be logged in to import from ${selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}`);
        return;
      }

      // Update task status with a temporary video ID for synthetic progress
      // Start at 5% to give immediate feedback
      updateTask(taskId, { 
        status: 'uploading',
        uploadStatus: `Downloading from ${selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}...`,
        progress: 5,
        videoId: tempVideoId  // Temporary ID for synthetic progress tracking
      });
      
      // Start gradual synthetic progress animation for download phase
      let currentProgress = 5; // Start at 5% for immediate feedback
      const maxProgress = 50;
      
      // Immediate first update to show activity
      setTimeout(() => {
        currentProgress += Math.random() * 5 + 3; // Initial jump of 3-8%
        updateTask(taskId, {
          progress: Math.round(currentProgress),
          uploadStatus: `Downloading from ${selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}... ${Math.round(currentProgress)}%`
        });
        updateSyntheticProgress(tempVideoId, currentProgress, `Downloading from ${selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}...`);
      }, 150); // First update after just 150ms
      
      progressInterval = setInterval(() => {
        if (currentProgress < maxProgress) {
          currentProgress += Math.random() * 3 + 1; // Random increment 1-4%
          currentProgress = Math.min(currentProgress, maxProgress);
          
          updateTask(taskId, {
            progress: Math.round(currentProgress),
            uploadStatus: `Downloading from ${selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}... ${Math.round(currentProgress)}%`
          });
          
          // Also update synthetic progress for consistency
          updateSyntheticProgress(tempVideoId, currentProgress, `Downloading from ${selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}...`);
        }
      }, 800); // Update every 800ms for smoother feeling

      // Get Firebase auth token (empty for guests)
      const token = currentUser ? await currentUser.getIdToken() : '';
      
      // Call the API to import the video
      const importHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      };
      const response = await fetch('/api/v1/import-url', {
        method: 'POST',
        headers: importHeaders,
        body: JSON.stringify({
          url: cleanUrl(url.trim(), selectedPlatform || undefined),
          platform: selectedPlatform
        }),
      });

      // Read as text first; then parse JSON with fallback-friendly errors
      const importText = await response.text();
      if (!response.ok) {
        let detail = 'Failed to import video';
        
        // Handle specific error codes
        if (response.status === 413) {
          detail = 'Upload limit reached! You\'ve reached your video upload limit for the free plan. Upgrade to continue importing videos.';
        } else {
          try {
            const errJson = JSON.parse(importText);
            detail = errJson.detail || detail;
            
            // Clean up common quota error messages
            if (detail.toLowerCase().includes('video limit') || detail.toLowerCase().includes('limit reached')) {
              detail = 'Upload limit reached! You\'ve reached your video upload limit for the free plan. Upgrade to continue importing videos.';
            }
          } catch {
            const trimmed = (importText || '').trim();
            const looksHtml = trimmed.startsWith('<') || trimmed.toLowerCase().includes('<html') || trimmed.toLowerCase().includes('doctype html');
            if (looksHtml || trimmed.length === 0) {
              detail = 'Temporary server error (502). We will continue in background if the job started.';
            } else {
              detail = trimmed;
            }
          }
        }
        throw new Error(detail);
      }
      let result: any;
      try {
        result = JSON.parse(importText);
      } catch {
        throw new Error('Unexpected response from server (could not parse JSON)');
      }
      
      // Pre-populate cache with placeholder data to avoid empty state flash
      const placeholderVideo = {
        id: result.video_id,
        filename: result.filename || `import_pending_${result.video_id}.mp4`,
        status: 'processing',
        created_at: new Date().toISOString(),
        metadata: {
          duration: 0,
          file_size: 0,
          resolution: '1920x1080',
          codec: 'unknown',
          source_platform: selectedPlatform || 'youtube'
        },
        thumbnail_url: null,
        clips_count: 0
      };
      (apiOptimizer as any)['cache'].set(`video_${result.video_id}`, {
        data: placeholderVideo,
        timestamp: Date.now(),
        expiry: Date.now() + 30000 // 30 seconds TTL for placeholder
      });
      
      // Navigate immediately - no empty state!
      navigate(`/videos/${result.video_id}`);
      
      // Let the progress animation continue to 50% in background
      const waitForProgressAnimation = new Promise<void>((resolve) => {
        const checkProgress = () => {
          if (currentProgress >= 50) {
            // Clear synthetic progress for download phase and stop interval
            if (progressInterval) {
              clearInterval(progressInterval);
            }
            clearSyntheticProgress(tempVideoId);
            resolve();
          } else {
            setTimeout(checkProgress, 500);
          }
        };
        checkProgress();
      });
      
      await waitForProgressAnimation;
      
      // Immediately notify parent component to refresh dashboard
      if (onImportStart) {
        onImportStart(url, selectedPlatform!);
      }

      // Refresh user profile to update video count in UI
      refreshUserProfile();
      
      // Update task with video details and transition to processing
      updateTask(taskId, {
        videoId: result.video_id,
        fileName: result.filename || `YouTube Video Import`,
        status: 'analyzing', 
        progress: 5, // Start low - backend will report actual progress
        uploadStatus: `✅ Download initiated - Processing video...`,
        processingStatus: `📥 Downloading and processing video...`
      });
      
      // Clear synthetic progress with temporary video ID since we now have the real one
      clearSyntheticProgress(tempVideoId);
      
      // Start video status polling to sync backend progress with frontend task
      addVideoStatusPolling(result.video_id, (statusData) => {
        // console.log(`🔄 [UrlImportCard SERVER] Received status update for ${result.video_id}:`, statusData);
        // Manually call updateTaskFromVideoStatus to ensure the widget gets updated
        updateTaskFromVideoStatus(result.video_id, statusData);
      }, 'high'); // High priority for active imports
      
      // Immediately check status to catch any progress that happened during the import call
      setTimeout(async () => {
        try {
          const currentStatus = await videoApi.getVideoStatus(result.video_id);
          // console.log(`🔍 [UrlImportCard SERVER] Immediate status check for ${result.video_id}:`, currentStatus);
          updateTaskFromVideoStatus(result.video_id, currentStatus);
        } catch (error) {
          // console.log(`⚠️ [UrlImportCard SERVER] Failed immediate status check:`, error);
        }
      }, 2000); // Check after 2 seconds
      
      const platformName = selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick';
      toast.success(`${platformName} video import started successfully!`);
      
      // Clear the form
      setUrl('');
      setSelectedPlatform(null);
      
    } catch (error) {
      // Clear synthetic progress on error and stop interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      clearSyntheticProgress(tempVideoId);

      // Attempt recovery by locating the server-created processing video
      try {
        const videos = await videoApi.getVideos();
        if (Array.isArray(videos) && videos.length > 0) {
          const candidate = videos.find((v: any) => v.status === 'processing' || v.status === 'uploaded');
          if (candidate?.id) {
            updateTask(taskId, {
              status: 'analyzing',
              progress: 10,
              uploadStatus: 'Continuing import in background...',
              processingStatus: 'Continuing import in background...',
              videoId: candidate.id
            });
            // Navigate immediately to show loading states
            navigate(`/videos/${candidate.id}`);
            return;
          }
        }
      } catch {
        // Ignore recovery errors
      }

      const rawMessage = error instanceof Error ? error.message : 'Failed to import video';
      const cleanMessage = rawMessage.replace(/<[^>]*>/g, '').trim().slice(0, 500) || 'Failed to import video';
      updateTask(taskId, {
        status: 'error',
        error: cleanMessage,
        uploadStatus: 'Import failed'
      });
      throw new Error(cleanMessage);
    }
  };

  const getPlatformIcon = () => {
    return <img src="/Twitch_Youtube.jpg" alt="Import from URL" className="w-full h-full object-cover rounded-xl" />;
  };

  const getAccentColor = () => {
    switch (selectedPlatform) {
      case 'youtube':
        return 'border-red-500';
      case 'twitch':
        return 'border-black';
      case 'kick':
        return 'border-red-500';
      default:
        return 'border-gray-300';
    }
  };

  return (
    <div className={`bg-white border-2 ${getAccentColor()} rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 ${className}`}>
      <div className="flex items-center mb-6">
        <div className="bg-black rounded-xl mr-4 w-16 h-16">
          {getPlatformIcon()}
        </div>
        <div>
          <h3 className="text-2xl font-bold text-black">Import from URL</h3>
          <p className="text-gray-600">Import videos from YouTube, Twitch, or Kick</p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="video-url" className="block text-lg font-semibold text-black mb-3">
            Video URL
          </label>
          <input
            type="url"
            id="video-url"
            value={url}
            onChange={handleUrlChange}
            placeholder="Paste YouTube, Twitch, or Kick URL here..."
            className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 
                       text-lg transition-all duration-200 bg-gray-50 focus:bg-white"
            disabled={isLoading}
          />
        </div>

        {selectedPlatform && (
          <div className="flex items-center space-x-3 text-base text-gray-700 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
            {!isPro && (selectedPlatform === 'twitch' || selectedPlatform === 'kick') ? (
              <Crown 
                className="w-5 h-5 text-red-600 cursor-pointer hover:scale-110 transition-transform" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/pricing');
                }}
              />
            ) : (
              <Check className="w-5 h-5 text-gray-600" />
            )}
            <span className="font-medium">
              {selectedPlatform === 'youtube' ? 'YouTube' : selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'} URL detected
              {!isPro && (selectedPlatform === 'twitch' || selectedPlatform === 'kick') && (
                <span 
                  className="text-sm text-red-600 font-semibold ml-2 cursor-pointer hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/pricing');
                  }}
                >
                  (Pro Required)
                </span>
              )}
              {selectedPlatform === 'youtube' && !isPro && (
                <span className="text-sm text-gray-500 font-semibold ml-2">(Free for all users!)</span>
              )}
              {currentMethod && (
                <span className="text-sm text-gray-500 ml-2">
                  ({currentMethod === 'client' ? 'Browser download' : 
                    currentMethod === 'mega-nuclear' ? '🚀 Advanced bypass' : 
                    'Standard download'})
                </span>
              )}
            </span>
          </div>
        )}

        {url && !selectedPlatform && (
                        <div className="flex items-center space-x-3 text-base text-gray-600 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">
              Unsupported platform. Please use YouTube, Twitch, or Kick URLs.
            </span>
          </div>
        )}



        <button
          onClick={handleImport}
          disabled={!url || !selectedPlatform || isLoading || (!isPro && (selectedPlatform === 'twitch' || selectedPlatform === 'kick'))}
          className="w-full bg-red-500 text-white px-8 py-4 rounded-xl font-semibold text-lg
                     hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all duration-200 flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>
                {currentMethod === 'client' 
                  ? 'Downloading in browser...' 
                  : currentMethod === 'mega-nuclear'
                  ? '🚀 Advanced bypass in progress...'
                  : 'Importing video...'
                }
              </span>
            </>
          ) : (
            <>
              {!isPro && (selectedPlatform === 'twitch' || selectedPlatform === 'kick') ? (
                <>
                  <Crown className="w-5 h-5" />
                  <span>Pro Required for {selectedPlatform === 'twitch' ? 'Twitch' : 'Kick'}</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Import Video</span>
                </>
              )}
            </>
          )}
        </button>

                          {/* <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-semibold text-black mb-2">Intelligent Import System:</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>• <span className="font-medium">Auto-escalation</span> through multiple strategies</p>
                      <p>• <span className="font-medium">Advanced bot protection</span> bypass when needed</p>
                      <p>• <span className="font-medium">Browser fallback</span> for maximum compatibility</p>
                    </div>
                  </div> */}

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-semibold text-black mb-2">Supported Platforms:</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>• <span className="text-black font-semibold">YouTube</span> videos and shorts <span className="text-gray-500">(Free)</span></p>
                      <p>• <span className="text-black font-semibold">Twitch</span> VODs and clips <span className="text-red-600 font-semibold">(Pro)</span></p>
                      <p>• <span className="text-black font-semibold">Kick</span> VODs and clips <span className="text-red-600 font-semibold">(Pro)</span></p>
                      <p>• Public videos only</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-semibold text-black mb-2">Auto Quality Selection:</p>
                    <div className="text-sm text-gray-700 space-y-1">
                      <p>• Videos ≤20 minutes: <span className="font-semibold text-red-600">1080p</span></p>
                      <p>• Videos 20min-1hr: <span className="font-semibold text-red-600">720p</span></p>
                      <p>• Videos &gt;1 hour: <span className="font-semibold text-red-600">480p</span></p>
                    </div>
                  </div>
      </div>
    </div>
  );
};

export default UrlImportCard; 